import { icons } from "@core/assets";
import { SideBar } from "@core/components";
import type { SideBarItem, MenuOption } from "@core/components";
import { useWorkspaceContext } from "@core/context";
import { useProjectContext } from "@modules/project/workspace/context";
import { useTranslate } from "@modules/project/workspace/hooks/useTranslate";
import { PROJECT_NAV, PROJECT_FOOTER_NAV } from "@modules/project/workspace/config";
import { useGetMemberProjects } from "@modules/project/portal/hooks";
import { useNavigate } from "react-router-dom";

export const ProjectSidebar = () => {
  const { workspaceId } = useWorkspaceContext();
  const { projectId, project, role } = useProjectContext();
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { data: projects } = useGetMemberProjects();

  const base = `/workspace/${workspaceId}/project-portal/${projectId}`;

  const mainSections: SideBarItem[] = PROJECT_NAV.flatMap((section) => {
    const allowedItems = section.items.filter((item) =>
      item.roles.includes(role),
    );
    if (allowedItems.length === 0) return [];
    return [
      {
        title: t(section.sectionKey as Parameters<typeof t>[0]),
        link: allowedItems.map((item) => ({
          name: t(item.labelKey as Parameters<typeof t>[0]),
          link: `${base}/${item.path}`,
          icon: icons[item.icon],
          type: "link" as const,
          ...(item.badge ? { badge: item.badge } : {}),
        })),
      },
    ];
  });

  const allowedFooterItems = PROJECT_FOOTER_NAV.filter((item) =>
    item.roles.includes(role),
  );

  const footerSection: SideBarItem[] =
    allowedFooterItems.length > 0
      ? [
          {
            title: t("sidebar.sections.other"),
            footer: true,
            link: allowedFooterItems.map((item) => ({
              name: t(item.labelKey as Parameters<typeof t>[0]),
              link: `${base}/${item.path}`,
              icon: icons[item.icon],
              type: "link" as const,
            })),
          },
        ]
      : [];

  const projectOptions: MenuOption[] = projects.map((p) => ({
    id: p.id,
    name: p.name,
    label: p.name,
  }));

  const switchProject = (name: string) => {
    const selected = projects.find((p) => p.name === name || p.id === name);
    if (!selected || selected.id === projectId) return;
    navigate(
      `/workspace/${workspaceId}/project-portal/${selected.id}/documents`,
      { replace: true },
    );
  };

  return (
    <SideBar
      backHome
      link={`/workspace/${workspaceId}/project-portal`}
      items={[...mainSections, ...footerSection]}
      workspaceHeaderTitle={project.name}
      className="md:w-[13rem] lg:w-[17.125rem]"
      is_switch
      menuOptions={projectOptions}
      onWorkspaceSelect={switchProject}
    />
  );
};
